// src/oura/trackerState.ts
//
// Live status overrides for the execution tracker, shared across views. Two
// stores: tracker tasks (the Timeline sheet) and snapshot actions (the Board
// kanban and the fix drawer). Marking work done manually, or performing the
// action inside the platform (approving a draft in the ActionDrawer), writes
// here; every subscribed view re-renders. Module-level like the aeo.ts job
// store: session-scoped in the prototype, PATCH /tracker/tasks/:id in the API.

import { useSyncExternalStore } from "react";
import type { Action } from "./contract";
import type { TaskStatus } from "./tracker";

type Listener = () => void;

function createStore<V>() {
    const map = new Map<string, V>();
    const listeners = new Set<Listener>();
    let version = 0;
    const emit = () => {
        version += 1;
        listeners.forEach((l) => l());
    };
    return {
        subscribe: (l: Listener) => {
            listeners.add(l);
            return () => {
                listeners.delete(l);
            };
        },
        getVersion: () => version,
        get: (id: string) => map.get(id),
        set: (id: string, v: V) => {
            map.set(id, v);
            emit();
        },
    };
}

const taskStore = createStore<TaskStatus>();
const actionStore = createStore<Action["status"]>();
const weekStore = createStore<number>(); // task -> the week it was moved to
const removedStore = createStore<true>(); // task -> removed from the plan
/**
 * Week -> expanded, once touched.
 *
 * Mirrored to localStorage, unlike the other stores here. The others describe
 * work (a status, a week move) and belong to the server; this one describes how
 * you left the page looking, and a module-level Map dies on reload — so an
 * expanded week folded itself every refresh, which read as the setting not
 * working at all.
 */
const expandStore = createStore<boolean>();

/**
 * A preference kept in one localStorage record, read once and written through.
 *
 * Rebuilt entry by entry rather than cast: a record written by an older build
 * must degrade to "no preference" for the rows it gets wrong, never throw on a
 * screen whose job is to show the plan.
 */
function persistedRecord<V>(storageKey: string, accept: (v: unknown) => v is V) {
    let cache: Record<string, V> | null = null;
    const load = (): Record<string, V> => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return {};
            const parsed: unknown = JSON.parse(raw);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
            const out: Record<string, V> = {};
            for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
                if (accept(v)) out[k] = v;
            }
            return out;
        } catch {
            return {};
        }
    };
    const record = (): Record<string, V> => {
        if (cache === null) cache = load();
        return cache;
    };
    return {
        get: (key: string): V | undefined => record()[key],
        set: (key: string, value: V): void => {
            record()[key] = value;
            try {
                localStorage.setItem(storageKey, JSON.stringify(record()));
            } catch {
                // A full or blocked storage costs the preference, not the screen.
            }
        },
    };
}

const expansionSaved = persistedRecord("oura:tracker-weeks", (v): v is boolean => typeof v === "boolean");

/**
 * Every entry is keyed by scope + id, so overrides can never bleed between
 * business drill-ins even if two businesses ever share raw ids (mock ids are
 * globally unique today, but the API's won't be guaranteed to be).
 */
function scopedKey(scope: string, id: string): string {
    return `${scope}:${id}`;
}

/**
 * The canonical scope for tracker/action overrides: business + assessment
 * run. Keying on the run (capturedAt) means a re-scan starts clean — stale
 * overrides from the previous assessment can never paint a fresh snapshot
 * as already done or rearranged.
 */
export function makeTrackerScope(businessId: string, capturedAt: string): string {
    return `${businessId}:${capturedAt}`;
}

/** Subscribe to task overrides; returns the effective status resolver. */
export function useTaskStatuses(scope: string): (id: string, base: TaskStatus) => TaskStatus {
    useSyncExternalStore(taskStore.subscribe, taskStore.getVersion);
    return (id, base) => taskStore.get(scopedKey(scope, id)) ?? base;
}

export function setTaskStatus(scope: string, id: string, status: TaskStatus): void {
    taskStore.set(scopedKey(scope, id), status);
}

/** Subscribe to action overrides; returns the effective status resolver. */
export function useActionStatuses(scope: string): (a: Action) => Action["status"] {
    useSyncExternalStore(actionStore.subscribe, actionStore.getVersion);
    return (a) => actionStore.get(scopedKey(scope, a.id)) ?? a.status;
}

/** Called when an action is performed inside the platform (drawer approve /
 *  mark as done) — the Board reflects it automatically. */
export function setActionStatus(scope: string, id: string, status: Action["status"]): void {
    actionStore.set(scopedKey(scope, id), status);
}

/** Subscribe to timeline placement: which week each task sits in, and whether
 *  it was removed from the plan. */
export function useTaskPlacement(scope: string): {
    weekOf: (id: string, base: number) => number;
    isRemoved: (id: string) => boolean;
} {
    useSyncExternalStore(weekStore.subscribe, weekStore.getVersion);
    useSyncExternalStore(removedStore.subscribe, removedStore.getVersion);
    return {
        weekOf: (id, base) => weekStore.get(scopedKey(scope, id)) ?? base,
        isRemoved: (id) => removedStore.get(scopedKey(scope, id)) === true,
    };
}

export function moveTask(scope: string, id: string, week: number): void {
    weekStore.set(scopedKey(scope, id), week);
}

export function removeTask(scope: string, id: string): void {
    removedStore.set(scopedKey(scope, id), true);
}

/**
 * Which weeks the accordion is showing.
 *
 * This lives here, next to the other tracker view state, for one reason: the
 * Tracker screen is conditionally rendered by the tab switch, so it UNMOUNTS
 * every time you look at another tab. Held in `useState`, an expanded Week 3
 * folded itself the moment you came back — the user had opened it, and the
 * product forgot. Component state cannot survive an unmount; a store outside
 * the component can.
 *
 * Only weeks a person has actually clicked are stored. An untouched week has no
 * entry, so it keeps following "the current week is open, the rest are folded"
 * — which stays right as the plan progresses. Recording all four up front would
 * freeze today's answer and stop the default ever moving again.
 *
 * Scoped like everything else here (business + run), so a re-scan opens on its
 * own current week rather than inheriting the last one's shape.
 */
export function useWeekExpansion(scope: string): (week: number, base: boolean) => boolean {
    useSyncExternalStore(expandStore.subscribe, expandStore.getVersion);
    return (week, base) => {
        const key = scopedKey(scope, String(week));
        // In-memory first, so a change made this session wins immediately;
        // localStorage second, which is what survives the reload.
        return expandStore.get(key) ?? expansionSaved.get(key) ?? base;
    };
}

export function setWeekExpanded(scope: string, week: number, expanded: boolean): void {
    const key = scopedKey(scope, String(week));
    expansionSaved.set(key, expanded);
    expandStore.set(key, expanded);
}

/**
 * Which layout of the plan Fixes shows: grouped by who moves next (the
 * default), or the week plan.
 *
 * Both layouts read the same statuses, placement and handlers, so this is
 * only how you left the page looking, and it is persisted for the same
 * reason the week accordion is: the screen unmounts on every tab switch.
 */
export type FixesView = "who" | "week";

const viewStore = createStore<FixesView>();
const viewSaved = persistedRecord("oura:tracker-view", (v): v is FixesView => v === "who" || v === "week");

export function useFixesView(scope: string): FixesView {
    useSyncExternalStore(viewStore.subscribe, viewStore.getVersion);
    return viewStore.get(scope) ?? viewSaved.get(scope) ?? "who";
}

export function setFixesView(scope: string, view: FixesView): void {
    viewSaved.set(scope, view);
    viewStore.set(scope, view);
}
